/**
 * CanniComply official logo lock-up.
 *
 * Vector rebuild of the owner-supplied brand mark (green shield with
 * checkmark + "CanniComply" wordmark + "STAFF ASSURANCE PLATFORM"
 * tagline). SVG so it prints cleanly at any resolution — critical for
 * A4 certificate export.
 *
 * Props:
 *   • variant:   "dark"  (green + white — on navy) [default]
 *                "light" (green + ink — on cream paper)
 *   • showTagline: whether to render the STAFF ASSURANCE PLATFORM strip
 *   • width:     total width in px
 */
export default function CannicomplyLogo({
  variant = "dark",
  showTagline = true,
  width = 200,
  fluid = false,
}) {
  const isLight = variant === "light";
  const inkColour = isLight ? "#0B1220" : "#FFFFFF";
  const green = "#34D57A";
  const greenDeep = "#0F5F4A";
  const tagline = isLight ? "#0B122099" : "#FFFFFF88";

  // Intrinsic aspect ratio 200 × 62 with tagline, 200 × 46 without.
  const height = showTagline ? (width * 62) / 200 : (width * 46) / 200;

  // In "fluid" mode the SVG scales to fill its parent width and preserves
  // aspect ratio via viewBox. `height` is expressed via CSS (not the SVG
  // attribute, which requires a numeric length).
  const dims = fluid
    ? { style: { display: "block", width: "100%", height: "auto" } }
    : { width, height };

  return (
    <svg
      viewBox={`0 0 200 ${showTagline ? 62 : 46}`}
      {...dims}
      preserveAspectRatio="xMidYMid meet"
      aria-label="CanniComply — Staff Assurance Platform"
      role="img"
    >
      {/* Shield mark */}
      <g transform="translate(2, 4)">
        <path
          d="M18 2 L34 8 V22 C34 30 27 36 18 40 C9 36 2 30 2 22 V8 Z"
          fill="none"
          stroke={green}
          strokeWidth="2.6"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Inner shadow accent */}
        <path
          d="M18 6 L30 10 V22 C30 28 25 32 18 35 C11 32 6 28 6 22 V10 Z"
          fill={green}
          fillOpacity="0.08"
        />
        {/* Checkmark */}
        <path
          d="M11 20 L16 25 L26 15"
          fill="none"
          stroke={green}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Wordmark */}
      <g transform="translate(46, 0)">
        <text
          x="0"
          y="28"
          fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          fontWeight="700"
          fontSize="24"
          letterSpacing="-0.5"
        >
          <tspan fill={inkColour}>Canni</tspan>
          <tspan fill={green}>Comply</tspan>
        </text>

        {showTagline && (
          <>
            {/* Divider rule */}
            <line
              x1="0"
              y1="40"
              x2="150"
              y2="40"
              stroke={isLight ? `${greenDeep}66` : `${green}55`}
              strokeWidth="0.8"
            />
            <text
              x="0"
              y="54"
              fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              fontWeight="600"
              fontSize="7"
              letterSpacing="1.4"
              fill={tagline}
            >
              STAFF ASSURANCE PLATFORM
            </text>
          </>
        )}
      </g>
    </svg>
  );
}
